// The consultation form editor's draft and the pure steps that change it. The
// draft lives in the editor until Publish (spec 1.3); nothing here talks to the
// API except toTemplateInput, which shapes the request.

import type { FormField, FormFieldType, FormOption, FormTemplate, FormTemplateInput } from '../../api/client'

export interface FormDraft {
  name: string
  drawn_signature: boolean
  guardian_under_18: boolean
  fields: FormField[]
}

export const FIELD_TYPES: { type: FormFieldType; label: string }[] = [
  { type: 'heading', label: 'Section heading' },
  { type: 'text', label: 'Text block' },
  { type: 'short', label: 'Short answer' },
  { type: 'long', label: 'Long answer' },
  { type: 'yes_no', label: 'Yes or no' },
  { type: 'checklist', label: 'Checklist' },
  { type: 'choice', label: 'Choose one' },
  { type: 'date', label: 'Date' },
  { type: 'statement', label: 'Statement to agree to' },
]

export const typeLabel = (type: FormFieldType) => FIELD_TYPES.find((t) => t.type === type)?.label ?? type

// New fields and options carry a local id so the list and preview can key on
// them. It is not sent: the API gives the field its real id.
const LOCAL = 'draft-'
let counter = 0
const localId = () => `${LOCAL}${++counter}`
const isLocal = (id: string) => id.startsWith(LOCAL)

export function blankDraft(): FormDraft {
  return { name: '', drawn_signature: true, guardian_under_18: false, fields: [] }
}

export function toDraft(t: Pick<FormTemplate, 'name' | 'drawn_signature' | 'guardian_under_18' | 'fields'>): FormDraft {
  return {
    name: t.name,
    drawn_signature: t.drawn_signature,
    guardian_under_18: t.guardian_under_18,
    fields: t.fields.map((f) => ({ ...f, options: f.options.map((o) => ({ ...o })) })),
  }
}

export function newOption(): FormOption {
  return { id: localId(), label: '' }
}

export function addField(fields: FormField[], type: FormFieldType): FormField[] {
  const field: FormField = {
    id: localId(),
    type,
    label: '',
    required: type === 'statement',
    health: false,
    details: false,
    none_option: false,
    options: type === 'checklist' || type === 'choice' ? [newOption()] : [],
  }
  return [...fields, field]
}

// moveField swaps a field with its neighbour; at either end it is a no-op.
export function moveField(fields: FormField[], index: number, delta: -1 | 1): FormField[] {
  const target = index + delta
  if (index < 0 || index >= fields.length || target < 0 || target >= fields.length) return fields
  const next = [...fields]
  ;[next[index], next[target]] = [next[target], next[index]]
  return next
}

export function updateField(fields: FormField[], index: number, patch: Partial<FormField>): FormField[] {
  return fields.map((f, i) => (i === index ? { ...f, ...patch } : f))
}

export function removeField(fields: FormField[], index: number): FormField[] {
  return fields.filter((_, i) => i !== index)
}

export function isDirty(original: FormDraft, draft: FormDraft): boolean {
  return JSON.stringify(original) !== JSON.stringify(draft)
}

// toTemplateInput is the request body: local ids are left off so the API makes
// real ones, and ids that came from the server are kept so the field stays the
// same field across versions.
export function toTemplateInput(draft: FormDraft, askEarlierSigners?: boolean): FormTemplateInput {
  return {
    name: draft.name.trim(),
    drawn_signature: draft.drawn_signature,
    guardian_under_18: draft.guardian_under_18,
    fields: draft.fields.map((f) => ({
      ...f,
      id: isLocal(f.id) ? '' : f.id,
      options: f.options.map((o) => ({ ...o, id: isLocal(o.id) ? '' : o.id })),
    })),
    ...(askEarlierSigners === undefined ? {} : { ask_earlier_signers: askEarlierSigners }),
  }
}
